import { formatDurationToHrMin } from "@utils/utils";
import React from "react";

import { AnalyticsStatCard } from "@fronterahealth/frontera-ui-components";

import { RbtSessionAnalyticsVideosDataType } from "@api/graphql/types-and-hooks";
import { CardContainer } from "@pages/VideoAnalytics/components/CardContainer";
import {
  COMMENTS_MAPPING,
  ProgressInputData,
  RBT_ANALYTICS_TITLES,
  calculatePercentageChange,
  transformToProgressData,
} from "@pages/VideoAnalytics/helpers";

interface CommentsAndFeedbackProps {
  currentData: RbtSessionAnalyticsVideosDataType | null | undefined;
  previousPeriodData: RbtSessionAnalyticsVideosDataType | null | undefined;
  isFetching: boolean;
  setIsRBTResToBCBAQueDialogOpen: (val: boolean) => void;
  setIsRBTResRateToBCBAQuesDialogOpen: (val: boolean) => void;
  setIsCommentOpen: (open: boolean) => void;
  setIsCommentDistributionOpen: (open: boolean) => void;
  setIsAvgQuestionAsked: (open: boolean) => void;
}

export const CommentsAndFeedback: React.FC<CommentsAndFeedbackProps> = ({
  currentData,
  previousPeriodData,
  isFetching,
  setIsRBTResToBCBAQueDialogOpen,
  setIsRBTResRateToBCBAQuesDialogOpen,
  setIsCommentOpen,
  setIsCommentDistributionOpen,
  setIsAvgQuestionAsked,
}) => {
  const commentData = currentData?.receivedCommentsDistributionCount as ProgressInputData;
  const commentsProgressData = currentData?.receivedCommentsDistributionCount
    ? transformToProgressData(commentData, COMMENTS_MAPPING)
    : undefined;

  return (
    <CardContainer title="Comments & Feedback">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2">
        <AnalyticsStatCard
          title={RBT_ANALYTICS_TITLES.AVG_RESPONSE_TIME_TO_BCBA_QUESTIONS}
          value={`${formatDurationToHrMin(currentData?.averageResponseTimeToBcbaQuestions ?? 0)}`}
          previousValue={
            previousPeriodData
              ? `${previousPeriodData ? formatDurationToHrMin(previousPeriodData?.averageResponseTimeToBcbaQuestions ?? 0) : "N/A"}`
              : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageResponseTimeToBcbaQuestions ?? 0),
                  Number(previousPeriodData?.averageResponseTimeToBcbaQuestions ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageResponseTimeToBcbaQuestions ?? 0),
                  Number(previousPeriodData?.averageResponseTimeToBcbaQuestions ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsRBTResToBCBAQueDialogOpen(true);
          }}
        />
        <AnalyticsStatCard
          title={RBT_ANALYTICS_TITLES.AVG_RESPONSE_RATE_TO_BCBA_QUESTIONS}
          value={`${currentData?.averageResponseRateToBcbaQuestions} %`}
          previousValue={
            previousPeriodData ? `${previousPeriodData?.averageResponseRateToBcbaQuestions ?? "N/A"} %` : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageResponseRateToBcbaQuestions ?? 0),
                  Number(previousPeriodData?.averageResponseRateToBcbaQuestions ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageResponseRateToBcbaQuestions ?? 0),
                  Number(previousPeriodData?.averageResponseRateToBcbaQuestions ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsRBTResRateToBCBAQuesDialogOpen(true);
          }}
        />
        <AnalyticsStatCard
          title={RBT_ANALYTICS_TITLES.AVG_QUESTIONS_ASKED_PER_VIDEO}
          value={`${currentData?.averageQuestionAskedPerVideo} questions`}
          previousValue={
            previousPeriodData ? `${previousPeriodData?.averageQuestionAskedPerVideo ?? "N/A"} questions` : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageQuestionAskedPerVideo ?? 0),
                  Number(previousPeriodData?.averageQuestionAskedPerVideo ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageQuestionAskedPerVideo ?? 0),
                  Number(previousPeriodData?.averageQuestionAskedPerVideo ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsAvgQuestionAsked(true);
          }}
        />
        <AnalyticsStatCard
          title={RBT_ANALYTICS_TITLES.AVG_COMMENT_RECEIVED_PER_VIDEO}
          value={`${currentData?.averageCommentsReceivedPerVideo} comments`}
          previousValue={
            previousPeriodData ? `${previousPeriodData?.averageCommentsReceivedPerVideo ?? "N/A"} comments` : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageCommentsReceivedPerVideo ?? 0),
                  Number(previousPeriodData?.averageCommentsReceivedPerVideo ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageCommentsReceivedPerVideo ?? 0),
                  Number(previousPeriodData?.averageCommentsReceivedPerVideo ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => setIsCommentOpen(true)}
        />
        <AnalyticsStatCard
          title={RBT_ANALYTICS_TITLES.TOTAL_COMMENTS_RECEIVED}
          value={`${currentData?.receivedCommentsDistributionCount?.totalComments} comments`}
          previousValue={
            previousPeriodData
              ? `${previousPeriodData?.receivedCommentsDistributionCount?.totalComments ?? "N/A"} comments`
              : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.receivedCommentsDistributionCount?.totalComments ?? 0),
                  Number(previousPeriodData?.receivedCommentsDistributionCount?.totalComments ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.receivedCommentsDistributionCount?.totalComments ?? 0),
                  Number(previousPeriodData?.receivedCommentsDistributionCount?.totalComments ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          breakdownData={commentsProgressData}
          showGraphLink
          onGraphClick={() => setIsCommentDistributionOpen(true)}
        />
      </div>
    </CardContainer>
  );
};
